// AsyncFacet — multi-select with async typeahead.
// Used for high-cardinality fields like agencies, vendors, NAICS codes.
// Selected values render as chips above the search input.
//
// Wiring: caller provides a `fetcher(query)` that returns options. We don't
// pull in a query lib here so this stays React-only and embeddable.

import { useEffect, useRef, useState } from 'react';
import type { FacetOption } from './MultiSelectFacet.js';

export interface AsyncFacetProps {
  field: string;
  label: string;
  /** Currently selected values (often these are codes, e.g. "541512"). */
  value: string[];
  /** Resolve a code → display label (for selected chips). Optional; if not
   *  given, the chip shows the code itself. */
  resolveLabel?: (value: string) => string | undefined;
  /** Async typeahead source. Called on every debounced keystroke. */
  fetcher: (q: string) => Promise<FacetOption[]>;
  onChange: (next: string[]) => void;
  placeholder?: string;
  debounceMs?: number;
}

export function AsyncFacet({
  field,
  label,
  value,
  resolveLabel,
  fetcher,
  onChange,
  placeholder = 'type to search…',
  debounceMs = 200,
}: AsyncFacetProps) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<FacetOption[]>([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (query.trim().length < 1) {
      setOptions([]);
      return;
    }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const opts = await fetcher(query);
        if (reqId.current === id) setOptions(opts);
      } catch {
        if (reqId.current === id) setOptions([]);
      } finally {
        if (reqId.current === id) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, fetcher, debounceMs]);

  const selected = new Set(value);
  const remove = (v: string) => onChange(value.filter((x) => x !== v));
  const add = (v: string) => {
    if (!selected.has(v)) onChange([...value, v]);
  };

  return (
    <fieldset
      data-facet={field}
      style={{
        border: 'none',
        borderTop: '1px solid var(--bd)',
        margin: 0,
        padding: '8px 10px 10px',
      }}
    >
      <legend
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span>{label}</span>
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--fg-3)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
            }}
          >
            CLR
          </button>
        )}
      </legend>

      {/* selected chips */}
      {value.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {value.map((v) => (
            <span
              key={v}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 20,
                padding: '0 4px 0 6px',
                background: 'var(--bg-pane-2)',
                border: '1px solid var(--bd-strong)',
                color: 'var(--fg-1)',
                fontFamily: 'var(--mono)',
                fontSize: 11,
              }}
            >
              <span title={resolveLabel?.(v)}>{resolveLabel?.(v) ?? v}</span>
              <button
                type="button"
                onClick={() => remove(v)}
                aria-label={`remove ${v}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--fg-3)',
                  fontSize: 12,
                  lineHeight: '12px',
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        style={{
          marginTop: 6,
          width: '100%',
          height: 24,
          padding: '0 6px',
          background: 'var(--bg-base)',
          border: '1px solid var(--bd-strong)',
          color: 'var(--fg-1)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
        }}
      />

      {/* Suggestion dropdown — lives in normal flow (no portal) so the rail
          scrolls with it. The host pane is already overflow:auto. */}
      {(loading || options.length > 0) && (
        <ul
          style={{
            listStyle: 'none',
            margin: '4px 0 0',
            padding: 0,
            border: '1px solid var(--bd-strong)',
            background: 'var(--bg-pane-2)',
            maxHeight: 220,
            overflow: 'auto',
          }}
        >
          {loading && options.length === 0 && (
            <li
              style={{ padding: 6, color: 'var(--fg-3)', fontFamily: 'var(--mono)', fontSize: 11 }}
            >
              loading…
            </li>
          )}
          {options.map((o) => {
            const isSelected = selected.has(o.value);
            return (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => add(o.value)}
                  disabled={isSelected}
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 22,
                    padding: '0 6px',
                    background: 'transparent',
                    border: 'none',
                    color: isSelected ? 'var(--fg-3)' : 'var(--fg-1)',
                    cursor: isSelected ? 'default' : 'pointer',
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    textAlign: 'left',
                  }}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.label ?? o.value}
                  </span>
                  {o.count !== undefined && (
                    <span style={{ color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
                      {o.count.toLocaleString()}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}
